import {
  EmptySectionCard,
  FAIconName,
  FireflyButton,
  InquiryDetailSectionHeader,
  NoteMaudCardV2,
  NoteMaudCardV2Props,
  SkeletonLoader,
} from '@equip.health/ui';
import { Section } from '~/components/common/Section';
import { PatientNoteItemTemplateRevisionForm } from '~/components/maud/patients/note/PatientNoteItemTemplateRevisionForm';
import { FromNotesType, getTooltipWrapper } from '../viewUtil';

export interface NotesProps {
  count: number;
  id: string;
  isLoading: boolean;
  notes: FromNotesType[];
  onAdd: () => void;
  onEdit: (note: NoteMaudCardV2Props) => void;
  onLoadMoreNotes: () => void;
  onVoid: (note: NoteMaudCardV2Props) => void;
}

interface NoteProps {
  note: FromNotesType;
  onEdit: (note: NoteMaudCardV2Props) => void;
  onVoid: (note: NoteMaudCardV2Props) => void;
}

const TASK_MANAGER = 'task manager';

const Note = ({ note, onEdit, onVoid }: NoteProps) => {
  const { noteTemplateRevisionData, noteTemplateRevisionId } = note;
  const tooltipWrapper =
    note?.assignee?.id.toLowerCase() !== TASK_MANAGER &&
    getTooltipWrapper(note?.assignee?.id, note?.createdDateTime);
  return (
    <NoteMaudCardV2
      avatarTooltipWrapper={tooltipWrapper}
      key={note.id}
      onEdit={() => onEdit(note)}
      onVoid={() => onVoid(note)}
      surveyContent={
        noteTemplateRevisionId && (
          <PatientNoteItemTemplateRevisionForm
            key={`${note.id}-template-revision`}
            minHeight="0px"
            noteId={note.id}
            noteTemplateRevisionData={noteTemplateRevisionData}
            noteTemplateRevisionId={noteTemplateRevisionId}
          />
        )
      }
      {...note}
    />
  );
};

export const Notes = ({
  count,
  id,
  isLoading,
  notes,
  onAdd,
  onEdit,
  onLoadMoreNotes,
  onVoid,
}: NotesProps) => {
  const TOPLEVEL_ID = `${id}__notes-section`;
  const hasMoreNotes = (notes ?? []).length < count;
  const noteCards =
    notes.length > 0 ? (
      notes.map((note) => (
        <Note key={note.id} note={note} onEdit={onEdit} onVoid={onVoid} />
      ))
    ) : (
      <EmptySectionCard
        caption="No notes have been created"
        id={`${TOPLEVEL_ID}-empty`}
      />
    );

  return (
    <Section id={TOPLEVEL_ID}>
      <InquiryDetailSectionHeader
        count={count}
        id={`${TOPLEVEL_ID}-header`}
        title="Notes"
      >
        <FireflyButton
          id={`${TOPLEVEL_ID}-add-button`}
          isDisabled={isLoading}
          leftIcon={FAIconName.plusSolid}
          onClick={onAdd}
          size="small"
          variant="secondary"
        >
          Add Note
        </FireflyButton>
      </InquiryDetailSectionHeader>
      <SkeletonLoader id={`${TOPLEVEL_ID}-loader`} isLoaded={!isLoading}>
        <Section id={`${TOPLEVEL_ID}-children`}>{noteCards}</Section>
      </SkeletonLoader>
      {hasMoreNotes && (
        <FireflyButton
          id={`${TOPLEVEL_ID}-load-more-button`}
          isDisabled={isLoading}
          onClick={onLoadMoreNotes}
          rightIcon={FAIconName.chevronDownRegular}
          size="small"
          variant="tertiary"
        >
          Show more notes
        </FireflyButton>
      )}
    </Section>
  );
};
